/*
  <tlm-animation>: the site's animations, silent MP4 loops that replace GIFs (snippets/animation.liquid).
  Used everywhere, advertorial loops included (it replaced their own <adv-video> on 2026-10-14):
  - The <video> gets its src only near the viewport (data-src), then loads and plays. It pauses off screen.
  - iOS/Android autoplay needs muted + playsinline: both are set as attributes and again as properties here.
  - When autoplay is refused (iOS Low Power Mode, "Auto-Play Video Previews" off, Reduce Motion, data saving...),
    the poster shows with a play button; a tap on the button or the video starts it. If even that fails,
    the native controls are shown. A loop that is only still loading never shows the button.
  - If the main file fails to load, the fallback (Shopify's 720p copy, data-src-fallback) is used.
*/
if (!customElements.get('tlm-animation')) {
  customElements.define(
    'tlm-animation',
    class TlmAnimation extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.button = this.querySelector('.tlm-animation__play');

        const video = this.video;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.visible = false;
        this.pausedByUs = false;

        if (this.button) this.button.addEventListener('click', () => this.userPlay());
        video.addEventListener('click', () => {
          if (video.paused) this.userPlay();
        });
        video.addEventListener('playing', () => this.toggleButton(false));
        video.addEventListener('pause', () => {
          if (this.visible && !this.pausedByUs) this.toggleButton(true);
        });
        video.addEventListener('error', () => this.useFallback());

        if (!('IntersectionObserver' in window)) {
          this.visible = true;
          this.load();
          this.autoPlay();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.visible = entry.isIntersecting;
              if (entry.isIntersecting) {
                this.load();
                this.autoPlay();
              } else if (!video.paused) {
                this.pausedByUs = true;
                video.pause();
              }
            });
          },
          { rootMargin: '300px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        clearTimeout(this.watchdog);
      }

      load() {
        const video = this.video;
        if (video.getAttribute('src') || !video.dataset.src) return;
        video.preload = 'auto';
        video.src = video.dataset.src;
        video.load();
      }

      useFallback() {
        const video = this.video;
        const fallback = video.dataset.srcFallback;
        if (!fallback || video.getAttribute('src') === fallback) return;
        video.src = fallback;
        video.load();
        if (this.visible) this.autoPlay();
      }

      autoPlay() {
        if (this.reduceMotion) {
          this.toggleButton(true);
          return;
        }
        this.play().catch(() => this.toggleButton(true));

        // Some browsers neither play nor reject (e.g. iOS Low Power Mode): show the button if nothing happens.
        // Only once the video has data: a loop that is still loading on a slow connection gets no button.
        this.armWatchdog(3000);
      }

      armWatchdog(delay) {
        clearTimeout(this.watchdog);
        this.watchdog = setTimeout(() => {
          if (!this.visible || !this.video.paused) return;
          if (this.video.readyState >= 2) {
            this.toggleButton(true);
          } else {
            this.armWatchdog(1500);
          }
        }, delay);
      }

      userPlay() {
        this.load();
        this.video.muted = true;
        this.play()
          .then(() => this.toggleButton(false))
          .catch(() => {
            this.toggleButton(false);
            this.video.controls = true;
          });
      }

      play() {
        this.pausedByUs = false;
        try {
          return this.video.play() || Promise.resolve();
        } catch (error) {
          return Promise.reject(error);
        }
      }

      toggleButton(show) {
        if (this.button) this.button.hidden = !show;
      }
    }
  );
}

/*
  <tlm-animation-scroller>: arrows for a sideways row of loops (snippets/animation-scroller.liquid).
  One step = one card plus the gap. The loops themselves are <tlm-animation> elements and load on their own.
*/
if (!customElements.get('tlm-animation-scroller')) {
  customElements.define(
    'tlm-animation-scroller',
    class TlmAnimationScroller extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.tlm-scroller__track');
        this.prev = this.querySelector('.tlm-scroller__btn--prev');
        this.next = this.querySelector('.tlm-scroller__btn--next');
        if (!this.track) return;
        this.update = this.update.bind(this);
        if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -this.step() }));
        if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: this.step() }));
        this.track.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.update);
      }

      step() {
        const card = this.track.querySelector('.tlm-scroller__card');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 14;
        return card ? card.getBoundingClientRect().width + gap : 300;
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 4;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      }
    }
  );
}
